import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Science as ExperimentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ExperimentList,
  TwoStepExperimentDialog,
  EditExperimentDialog,
  ExperimentDetailsDialog,
} from './components';
import { useExperiments } from './hooks/useExperiments';
import { useTemplates } from './hooks/useTemplates';
// Migrated to MV endpoint - no longer using TemplatesDataProvider
// import { useTemplatesData } from '../../providers/TemplatesDataProvider';
import { createIOSButtonStyles } from '../../theme/iosButtonStyles';
import { useTheme } from '@mui/material/styles';
import { SPACING } from '../../theme';

const ExperimentManager = () => {
  const theme = useTheme();
  const iosButtonStyles = createIOSButtonStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [errorMessage, setErrorMessage] = useState(null);

  // Custom hooks
  const {
    experiments,
    loading: experimentsLoading,
    error: experimentsError,
    fetchExperiments,
    createExperiment,
    updateExperiment,
    startExperiment,
    pauseExperiment,
    stopExperiment,
  } = useExperiments();

  // Use MV-based hook via useTemplates (which now uses useTemplatesForExperiments)
  // This provides templates with analytics from MV endpoint
  const { templates, loading: templatesLoading } = useTemplates();

  // Debounce search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load experiments data on component mount
  useEffect(() => {
    fetchExperiments({
      page: page + 1,
      page_size: rowsPerPage,
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: debouncedSearchQuery || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
    });
    // Templates are now loaded by higher-level provider - no need to fetch here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    statusFilter,
    debouncedSearchQuery,
    sortBy,
    sortOrder,
  ]);

  // Handle pre-selected template from navigation
  useEffect(() => {
    if (location.state?.selectedTemplateId) {
      setCreateDialogOpen(true);
    }
  }, [location.state]);

  // Event handlers
  const handleCreateExperiment = async experimentData => {
    try {
      await createExperiment(experimentData);
      setCreateDialogOpen(false);
      fetchExperiments({
        page: page + 1,
        page_size: rowsPerPage,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: debouncedSearchQuery || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
    } catch (error) {
      // Error creating experiment
    }
  };

  const handleEditExperiment = async (experimentId, experimentData) => {
    try {
      setErrorMessage(null);
      await updateExperiment(experimentId, experimentData);
      setEditDialogOpen(false);
      setSelectedExperiment(null);
      fetchExperiments({
        page: page + 1,
        page_size: rowsPerPage,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: debouncedSearchQuery || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
    } catch (error) {
      // Extract error message from API response
      const errorMsg =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to update experiment. Please check if the experiment is active and try again.';
      setErrorMessage(errorMsg);
      // eslint-disable-next-line no-console
      console.error('Error updating experiment:', error);
    }
  };

  const handleStatusChange = async (experimentId, action) => {
    try {
      switch (action) {
        case 'start':
          await startExperiment(experimentId);
          break;
        case 'pause':
          await pauseExperiment(experimentId);
          break;
        case 'stop':
          await stopExperiment(experimentId);
          break;
        default:
          break;
      }
      fetchExperiments({
        page: page + 1,
        page_size: rowsPerPage,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: debouncedSearchQuery || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
    } catch (error) {
      // Error with experiment action - show error message to user
      // eslint-disable-next-line no-console
      console.error('Experiment action failed:', error);
      // TODO: Add toast notification or error alert
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = newStatus => {
    setStatusFilter(newStatus);
    setPage(0);
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleSortChange = (column, order) => {
    setSortBy(column);
    setSortOrder(order);
    setPage(0);
  };

  const handleExperimentClick = experiment => {
    setSelectedExperiment(experiment);
    setDetailsDialogOpen(true);
  };

  const handleEditClick = experiment => {
    setSelectedExperiment(experiment);
    setEditDialogOpen(true);
  };

  // Remove full-page block on loading so actions remain available

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <ExperimentIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Experiment Manager
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage A/B testing experiments for your templates
        </Typography>
      </Box>

      {/* Filters and Actions - Modern Design */}
      <Paper
        sx={{
          p: SPACING.lg,
          mb: 3,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Search Experiments"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search by name..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={6}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                data-testid="create-experiment-btn"
                sx={{
                  ...iosButtonStyles.primary,
                  textTransform: 'none',
                  fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
                }}
              >
                Create Experiment
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Display */}
      {experimentsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {experimentsError}
        </Alert>
      )}

      {/* Experiments List */}
      {experimentsLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <ExperimentList
          experiments={experiments?.experiments || []}
          loading={false}
          onExperimentClick={handleExperimentClick}
          onEditClick={handleEditClick}
          onStatusChange={handleStatusChange}
          onStatusFilterChange={handleStatusFilterChange}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={experiments?.total_count || 0}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          statusFilter={statusFilter}
        />
      )}

      {/* Dialogs */}
      <TwoStepExperimentDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          // Clear the navigation state when closing
          if (location.state?.selectedTemplateId) {
            navigate('/experiments', { replace: true });
          }
        }}
        onSubmit={handleCreateExperiment}
        selectedTemplateId={location.state?.selectedTemplateId}
        data-testid="two-step-experiment-dialog"
      />

      <EditExperimentDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedExperiment(null);
          setErrorMessage(null);
        }}
        onSubmit={handleEditExperiment}
        experiment={selectedExperiment}
        templates={templates?.templates || []}
        templatesLoading={templatesLoading}
        errorMessage={errorMessage}
      />

      <ExperimentDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedExperiment(null);
        }}
        experiment={selectedExperiment}
        onStatusChange={handleStatusChange}
      />
    </Container>
  );
};

export default ExperimentManager;
